import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Standard stable worker source
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getCleanUrl = (url) => {
  if (!url) return '';
  if (url.includes('http')) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const cleanBackend = backend.endsWith('/') ? backend.slice(0, -1) : backend;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${cleanBackend}${cleanUrl}`;
};

const PDFViewer = ({ pdfUrl }) => {
  const { t } = useLanguage();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  };

  return (
    <div className="w-full h-full bg-[#374151] flex flex-col">
      <div className="bg-[#1F2937] p-3 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            className="bg-[#374151] hover:bg-[#4B5563] text-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-white text-sm">
            {pageNumber} / {numPages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            className="bg-[#374151] hover:bg-[#4B5563] text-white"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto flex justify-center items-start p-4">
        <TransformWrapper
          initialScale={1}
          minScale={0.5}
          maxScale={3}
          centerOnInit={true}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <div className="fixed bottom-6 right-6 z-10 flex flex-col space-y-2">
                <Button
                  size="icon"
                  onClick={() => zoomIn()}
                  className="bg-[#B91C1C] hover:bg-[#991B1B] text-white shadow-lg"
                >
                  <ZoomIn className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  onClick={() => zoomOut()}
                  className="bg-[#B91C1C] hover:bg-[#991B1B] text-white shadow-lg"
                >
                  <ZoomOut className="h-5 w-5" />
                </Button>
                <Button
                  size="icon"
                  onClick={() => resetTransform()}
                  className="bg-[#B91C1C] hover:bg-[#991B1B] text-white shadow-lg"
                >
                  <RotateCcw className="h-5 w-5" />
                </Button>
              </div>
              <TransformComponent>
                <Document
                  file={getCleanUrl(pdfUrl)}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div className="text-white">{t('loading')}</div>}
                >
                  <Page
                    pageNumber={pageNumber}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={800}
                  />
                </Document>
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </div>
    </div>
  );
};

export default PDFViewer;
